<template>
  <div class="substep-download-and-track-time">
    <div class="substep-header">
      <h3 data-testid="step-title">Install the desktop app and track time to a project</h3>
      <p>Start the timer on the desktop app to confirm your setup</p>
    </div>

    <div class="substep-body">
      <AppAlert
        v-if="!timeTracked"
        type="info"
        icon-classes="hi hi-info"
        message="We'll let you know the moment we can see you tracking time."
      />

      <div class="setup-story-card">
        <div class="setup-steps">
          <div
            class="setup-step"
            :class="{ completed: downloadDone }"
            data-testid="substep-download"
          >
            <span class="setup-step-icon">
              <i :class="downloadDone ? 'hi hi-check' : 'hi hi-export'" />
            </span>
            <span class="setup-step-label">Download the app</span>
            <span
              v-if="!downloadDone"
              class="setup-step-actions"
            >
              <button
                type="button"
                class="btn btn-default btn-sm"
                data-testid="already-downloaded-button"
                @click="confirmDownload(true)"
              >
                I already did this
              </button>
              <a
                class="btn btn-primary btn-sm"
                :href="downloadUrl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-button"
                @click="confirmDownload(false)"
              >
                Download
              </a>
            </span>
          </div>

          <div
            class="setup-step"
            :class="{ completed: timeTracked, pending: !downloadDone }"
            data-testid="substep-track"
          >
            <span class="setup-step-icon">
              <i :class="timeTracked ? 'hi hi-check' : 'hi hi-play'" />
            </span>
            <span class="setup-step-label">Press play to start the timer</span>
          </div>
        </div>

        <div
          class="signal-status"
          :class="{ tracked: timeTracked }"
          data-testid="signal-status"
        >
          <template v-if="timeTracked">
            <i class="hi hi-check-circle" />
            <span>Got it — we can see you're tracking time.</span>
          </template>
          <template v-else>
            <span class="signal-dot" />
            <span>Waiting to hear back from your desktop app…</span>
          </template>
        </div>
      </div>

      <button
        type="button"
        class="need-help-trigger"
        data-testid="need-help-trigger"
        @click="showHelpModal = true"
      >
        Need help, can't track, or doesn't apply?
      </button>
    </div>

    <TrackTimeNeedHelpModal
      :show="showHelpModal"
      :quick-start-guide-url="quickStartGuideUrl"
      @close="showHelpModal = false"
      @track-link="trackHelpLink"
      @continue-without-tracking="$emit('skipSubstep')"
    />
  </div>
</template>

<script>
import { UserOrganizationsResource } from '~/resources';
import TrackTimeNeedHelpModal from '~/components/growth_wizard/getting_started/substeps/components/TrackTimeNeedHelpModal.vue';

const POLL_INTERVAL_MS = 10000;

export default {
  name: 'DownloadAndTrackTime',
  components: { TrackTimeNeedHelpModal },
  props: {
    downloadUrl: {
      type: String,
      required: true,
    },
    downloadDialog: {
      type: String,
      required: true,
    },
    userOrganizationId: {
      type: Number,
      required: true,
    },
    downloadConfirmed: {
      type: Boolean,
      default: false,
    },
    timeTracked: {
      type: Boolean,
      default: false,
    },
    hasProject: {
      type: Boolean,
      default: false,
    },
    quickStartGuideUrl: {
      type: String,
      required: true,
    },
    reloadSubstep: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showHelpModal: false,
      downloadClicked: false,
      pollTimer: null,
    };
  },
  computed: {
    downloadDone() {
      return this.downloadConfirmed || this.downloadClicked;
    },
  },
  watch: {
    timeTracked(detected) {
      if (detected) {
        this.stopPolling();
        this.$emit('completeSubstep');
      }
    },
  },
  mounted() {
    if (this.timeTracked || !this.hasProject) {
      this.$emit('completeSubstep');

      return;
    }

    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.pollTimer = setInterval(() => this.reloadSubstep(), POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    trackHelpLink(link) {
      this.$emit('trackWizardEvent', 'User onboarding help link clicked', {
        type: 'user_getting_started',
        link,
      });
    },
    async confirmDownload(alreadyDownloaded) {
      this.$emit('trackWizardEvent', 'Desktop app download clicked', {
        experiment: true,
        source: 'user_getting_started_track_time',
        already_downloaded: alreadyDownloaded,
      });
      this.downloadClicked = true;

      try {
        await UserOrganizationsResource.dismissDialog(
          { id: this.userOrganizationId },
          { dialog: this.downloadDialog },
          { abortReload: true },
        );
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage("We couldn't save that. It may reappear if you reload the page.");
        }
      }
    },
  },
};
</script>
